import { useTranslation } from 'react-i18next';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import OrderPreviewMatrix from '@/components/order/OrderPreviewMatrix';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { AdminOrderSummary } from '@/services/adminService';
import { getOrderStatusLabel, type AdminWorkspaceCurrencyCode, type AdminWorkspaceOrderAction, type AdminWorkspaceOrderGroup } from './types';
import type { AdminOrderStatus } from '@/services/adminService';
import { formatAdminDateTime, formatCurrencyAmount, formatPrice } from './utils';

interface OrderGroupsSectionProps {
  groups: AdminWorkspaceOrderGroup[];
  status: 'idle' | 'loading' | 'error';
  currencyCode: AdminWorkspaceCurrencyCode;
  usdToUahRate: number | null;
  orderStatusSavingId: string | null;
  onOrderGroupPrintLabels: (statusKey: string, count: number) => void;
  onOrderStatusChange: (orderId: string, status: 'PAID' | 'IN_PRODUCTION' | 'PRINTED' | 'IN_DELIVERY' | 'DELIVERED') => void;
  onOrderAction: (action: AdminWorkspaceOrderAction, orderId: string) => void;
  onOpenOrderItems: (displayCode: string) => void;
}

const READONLY_ORDER_TARGET_STATUSES = new Set<AdminOrderStatus>(['NEW', 'PRINTED', 'RETURNED', 'CANCELLED']);

const formatOrderSummaryAmount = (
  amountUsd: number | null,
  currencyCode: AdminWorkspaceCurrencyCode,
  confirmedUsdToUahRate: number | null,
): string => {
  if (amountUsd == null || !Number.isFinite(amountUsd)) {
    return '—';
  }

  if (currencyCode === 'USD') {
    return formatPrice(amountUsd, 'USD', null);
  }

  if (confirmedUsdToUahRate == null || !Number.isFinite(confirmedUsdToUahRate) || confirmedUsdToUahRate <= 0) {
    return '—';
  }

  return formatCurrencyAmount(amountUsd * confirmedUsdToUahRate, 'UAH', 'UAH', null);
};

const isReadonlyOrderStatusOption = (
  currentStatus: AdminOrderStatus,
  optionStatus: AdminOrderStatus,
  allItemsReadyForDelivery: boolean,
): boolean => {
  if (currentStatus === optionStatus) {
    return false;
  }

  if (optionStatus === 'IN_DELIVERY' || optionStatus === 'DELIVERED') {
    return !allItemsReadyForDelivery;
  }

  return READONLY_ORDER_TARGET_STATUSES.has(optionStatus);
};

const ShipmentDetails = ({ order }: { order: AdminOrderSummary }) => {
  if (!order.shippingProvider) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help font-medium text-foreground underline decoration-dotted">
            {order.shippingProvider}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1 text-xs">
            {order.ukrposhtaBarcode ? <p><span className="font-semibold text-muted-foreground">Barcode:</span> {order.ukrposhtaBarcode}</p> : null}
            {order.ukrposhtaShipmentUuid ? <p><span className="font-semibold text-muted-foreground">UUID:</span> {order.ukrposhtaShipmentUuid}</p> : null}
            {order.novapostNumber ? <p><span className="font-semibold text-muted-foreground">Number:</span> {order.novapostNumber}</p> : null}
            {order.novapostShipmentUuid ? <p><span className="font-semibold text-muted-foreground">UUID:</span> {order.novapostShipmentUuid}</p> : null}
            {!order.ukrposhtaBarcode && !order.novapostNumber ? <p>No shipment details yet</p> : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const OrderActionMenu = ({ order, onOrderAction }: {
  order: AdminOrderSummary;
  onOrderAction: (action: AdminWorkspaceOrderAction, orderId: string) => void;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8">
        <MoreVertical className="size-4" />
        <span className="sr-only">Open actions</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {order.fulfillmentMode === 'PICKUP' ? (
        <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('show_ukrposhta_summary', order.id); }}>
          Shipment summary
        </DropdownMenuItem>
      ) : order.shippingProvider === 'UKRPOSHTA' ? (
        <>
          <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('show_ukrposhta_summary', order.id); }}>
            Shipment summary
          </DropdownMenuItem>
          {order.ukrposhtaBarcode ? (
            <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('track_shipment', order.id); }}>
              Track shipment
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('set_manual_tracking', order.id); }}>
            Set shipment tracking
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('create_ukrposhta', order.id); }} disabled={!!order.ukrposhtaShipmentUuid}>
            Create Ukrposhta Shipment
          </DropdownMenuItem>
          {order.ukrposhtaShipmentUuid ? (
            <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('print_ukrposhta', order.id); }}>
              Print Ukrposhta Label
            </DropdownMenuItem>
          ) : null}
        </>
      ) : null}
      {order.shippingProvider === 'NOVAPOST' ? (
        <>
          <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('show_ukrposhta_summary', order.id); }}>
            Shipment summary
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('set_manual_tracking', order.id); }}>
            Set shipment tracking
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('create_novapost', order.id); }}>
            Create NovaPost Shipment
          </DropdownMenuItem>
          {order.novapostShipmentUuid ? (
            <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('print_novapost', order.id); }}>
              Print NovaPost Label
            </DropdownMenuItem>
          ) : null}
        </>
      ) : null}
      <DropdownMenuItem disabled onSelect={(event) => { event.preventDefault(); onOrderAction('print_label', order.id); }}>
        Print label
      </DropdownMenuItem>
      <DropdownMenuItem disabled onSelect={(event) => { event.preventDefault(); onOrderAction('select_pack', order.id); }}>
        Select pack
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={(event) => { event.preventDefault(); onOrderAction('process_refund', order.id); }}>
        Process Refund
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const OrderGroupsSection = ({
  groups,
  status,
  currencyCode,
  orderStatusSavingId,
  onOrderGroupPrintLabels,
  onOrderStatusChange,
  onOrderAction,
  onOpenOrderItems,
}: OrderGroupsSectionProps) => {
  const { t } = useTranslation();
  if (groups.length === 0 && status !== 'loading') {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No orders found.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {groups.map((group) => (
        <Card key={group.key}>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle>Status: {group.key}</CardTitle>
                <CardDescription>{group.orders.length} order{group.orders.length === 1 ? '' : 's'}</CardDescription>
              </div>
              <Button variant="secondary" size="sm" onClick={() => onOrderGroupPrintLabels(group.key, group.orders.length)} disabled={group.orders.length === 0}>
                Print labels
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Preview</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Item statuses</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Total / Shipment cost</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.orders.map((order) => {
                  const customerName = order.customer?.name ?? order.shippingAddress?.firstName ?? null;
                  const customerLabel = customerName
                    ? `${customerName}${order.shippingAddress?.lastName ? ` ${order.shippingAddress.lastName}` : ''}`
                    : order.customer?.email ?? 'Unknown customer';
                  const itemStatuses = order.orderItems.map((item) => item.status);
                  const statusCounts = itemStatuses.reduce<Map<string, number>>((counts, statusLabel) => {
                    counts.set(statusLabel, (counts.get(statusLabel) ?? 0) + 1);
                    return counts;
                  }, new Map());
                  const uniqueStatuses = new Set(itemStatuses);
                  const hasMixedStatuses = uniqueStatuses.size > 1;
                  const allItemsReadyForDelivery = order.orderItems.length > 0 && order.orderItems.every(
                    (item) => item.status === 'PRINTED' || item.status === 'IN_DELIVERY' || item.status === 'DELIVERED',
                  );
                  const orderStatusValue = order.status;

                  return (
                    <TableRow key={order.id} className={hasMixedStatuses ? 'bg-amber-50/70' : undefined}>
                      <TableCell>
                        <OrderPreviewMatrix
                          previewUrls={order.previewUrls}
                          itemCount={order.orderItems.length}
                          alt="Order preview"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{customerLabel}</p>
                          {order.customer?.email ? <p className="text-xs text-muted-foreground">{order.customer.email}</p> : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <p>ID: {order.displayCode}</p>
                          <p>Confirmed: {formatAdminDateTime(order.confirmedAt ?? order.createdAt)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {order.shippingAddress ? (
                          <div className="space-y-1 text-xs text-muted-foreground">
                            <span title={`${order.shippingAddress.street1}${order.shippingAddress.street2 ? `, ${order.shippingAddress.street2}` : ''}, ${order.shippingAddress.city}, ${order.shippingAddress.region}, ${order.shippingAddress.postalCode}, ${order.shippingAddress.countryCode}`}>
                              {order.shippingAddress.countryCode}, {order.shippingAddress.city}
                            </span>
                            <ShipmentDetails order={order} />
                          </div>
                        ) : order.fulfillmentMode === 'PICKUP' ? (
                          <span className="text-xs text-muted-foreground">Self pickup</span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {itemStatuses.length === 0 ? (
                            <span className="text-xs text-muted-foreground">No items</span>
                          ) : (
                            Array.from(statusCounts.entries()).map(([statusLabel, count]) => (
                              <span key={statusLabel} className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                {statusLabel}: {count}
                              </span>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          key={`${order.id}:${orderStatusValue}:${orderStatusSavingId === order.id ? 'saving' : 'idle'}`}
                          value={orderStatusValue}
                          onValueChange={(value) => onOrderStatusChange(order.id, value as 'PAID' | 'IN_PRODUCTION' | 'PRINTED' | 'IN_DELIVERY' | 'DELIVERED')}
                          disabled={orderStatusSavingId === order.id || order.status === 'NEW'}
                        >
                          <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder={t('adminOrdersWorkspace.orderStatus.placeholder', 'Select status')} />
                          </SelectTrigger>
                          <SelectContent>
                            {(['NEW', 'PAID', 'IN_PRODUCTION', 'PRINTED', 'IN_DELIVERY', 'DELIVERED', 'RETURNED', 'CANCELLED'] as const).map((statusOption) => (
                              <SelectItem
                                key={statusOption}
                                value={statusOption}
                                disabled={isReadonlyOrderStatusOption(order.status, statusOption, allItemsReadyForDelivery)}
                              >
                                {getOrderStatusLabel(t, statusOption)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatAdminDateTime(order.updatedAt)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{formatOrderSummaryAmount(order.orderAmountUsd, currencyCode, order.confirmedUsdToUahRate)}</span>
                        <span> / </span>
                        <span>{formatOrderSummaryAmount(order.shippingAmountUsd, currencyCode, order.confirmedUsdToUahRate)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-col items-end gap-2">
                          <OrderActionMenu order={order} onOrderAction={onOrderAction} />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => onOpenOrderItems(order.displayCode)}
                          >
                            to Item
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </>
  );
};

export default OrderGroupsSection;
